// @flow

import { useEffect, useMemo } from 'react';

import { useFormContext, Controller } from 'react-hook-form';

import { useDispatch } from 'react-redux';
import { Checkbox, CheckboxGroup, Text, View } from 'reshaped';

import {
  selectContracts,
  computeLabel,
  contractsResource,
  fetchContracts,
} from '@gandi/contracts';
import { useCountry } from '@gandi/react-places';
import { Link } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

export function ResellerCheckbox({
  scrollIntoView,
  createMode,
}: {|
  scrollIntoView: (block?: 'start' | 'end') => void,
  createMode: boolean,
|}): React.MixedElement | null {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const form = useFormContext();
  const country = form.watch('country');

  const { buId } = useCountry(country);

  const contractParams = useMemo(
    () => ({
      buId: buId ? buId.toString() : undefined,
      contractId: 'reseller',
    }),
    [buId],
  );
  const contractLabel = computeLabel(contractParams);

  const contracts = useAppSelector(state =>
    selectContracts(state, contractLabel),
  );

  // In create mode, g2 is already displayed above, so we exclude it here to avoid displaying it twice.
  const contractsToDisplay = createMode
    ? contracts.filter(contract => contract.identifier !== 'g2')
    : contracts;

  const shouldFetchContracts = useAppSelector(state =>
    contractsResource.selectShouldFetch(state, contractLabel),
  );

  const isFetchingContracts = useAppSelector(state =>
    contractsResource.selectIsFetching(state, contractLabel),
  );

  const isLoadedContracts = useAppSelector(state =>
    contractsResource.selectIsLoaded(state, contractLabel),
  );

  const isFailureContracts = useAppSelector(state =>
    contractsResource.selectIsFailure(state, contractLabel),
  );

  useEffect(() => {
    if (shouldFetchContracts && contractParams.buId) {
      dispatch(fetchContracts(contractParams));
    }
  }, [contractParams, shouldFetchContracts, dispatch]);

  const isPending =
    !isLoadedContracts && (!isFailureContracts || isFetchingContracts);
  const hasContracts = contracts && contracts.length > 0;
  useEffect(() => {
    if (!isPending && !hasContracts) {
      // Update reseller to true if no contract
      form.setValue('reseller', true, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [isPending, hasContracts, form]);

  useEffect(() => {
    if (isLoadedContracts) scrollIntoView();
  }, [isLoadedContracts, scrollIntoView]);

  if (!isPending && !hasContracts) {
    return null;
  }

  return (
    <div>
      <Text as="p" variant="body-2" color="neutral" weight="bold">
        {__('Contract acceptance')}
      </Text>
      <View paddingBlock={2}>
        <Text as="p" variant="body-2" color="neutral">
          {__('You must read and accept the reseller contract to continue.')}
        </Text>
      </View>

      <CheckboxGroup
        name="resellerContracts"
        onChange={({ value }) => {
          // set reseller to true if all the contracts checked
          const allChecked = value?.length === contracts.length;
          form.setValue('reseller', allChecked, {
            shouldValidate: true,
            shouldDirty: true,
          });
          scrollIntoView('end');
        }}
      >
        <View gap={1}>
          {contractsToDisplay.map(contract => (
            <Checkbox key={contract.name} value={contract.name}>
              <View direction="row" gap={1} paddingBlock={3}>
                {contract.identifier === 'reseller' ? (
                  <>
                    <Text variant="body-2">{contract?.label}</Text>
                    <Link
                      href={contract?.href}
                      attributes={{
                        target: '_blank',
                      }}
                    >
                      <Text variant="body-2" align="end">
                        {__('Reseller contract')}
                      </Text>
                    </Link>
                  </>
                ) : (
                  <Link
                    href={contract?.href}
                    attributes={{
                      target: '_blank',
                    }}
                  >
                    <Text variant="body-2">{contract?.label}</Text>
                  </Link>
                )}
              </View>
            </Checkbox>
          ))}
        </View>
      </CheckboxGroup>

      {/* Validation for the contracts */}
      <Controller
        name="reseller"
        control={form.control}
        defaultValue={false}
        rules={{
          validate: value =>
            value === true ||
            __(
              '<for-translators-use>%(count)s</for-translators-use>Please accept all contracts above to proceed.',
              { count: contracts.length },
            ),
        }}
        render={({ fieldState }) =>
          fieldState.error && (
            <View paddingTop={3}>
              <Text color="critical" variant="body-2">
                {fieldState.error.message}
              </Text>
            </View>
          )
        }
      />
    </div>
  );
}
